"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Metamodel = void 0;
var tslib_1 = require("tslib");
var ManagedType_1 = require("./ManagedType");
var EntityType_1 = require("./EntityType");
var binding_1 = require("../binding");
var ModelBuilder_1 = require("./ModelBuilder");
var DbIndex_1 = require("./DbIndex");
var util_1 = require("../util");
var connector_1 = require("../connector");
var message = tslib_1.__importStar(require("../message"));
var Metamodel = /** @class */ (function (_super) {
    tslib_1.__extends(Metamodel, _super);
    /**
     * Constructs a new metamodel instance which represents the complete schema of one baqend app
     * @param entityManagerFactory
     */
    function Metamodel(entityManagerFactory) {
        var _this = _super.call(this) || this;
        /**
         * Defines if the Metamodel has been finalized
         */
        _this.isInitialized = false;
        _this.entities = {};
        _this.embeddables = {};
        _this.baseTypes = {};
        _this.enhancer = new binding_1.Enhancer();
        _this.entityManagerFactory = entityManagerFactory;
        return _this;
    }
    /**
     * Prepare the Metamodel for custom schema creation
     * @param jsonMetamodel initialize the metamodel with the serialized json schema
     * @return
     */
    Metamodel.prototype.init = function (jsonMetamodel) {
        if (this.isInitialized) {
            throw new Error('Metamodel is already initialized.');
        }
        this.fromJSON(jsonMetamodel || []);
        this.isInitialized = true;
    };
    /**
     * @param arg
     * @return
     */
    Metamodel.prototype.getRef = function (arg) {
        var ref;
        if (typeof arg === 'string') {
            ref = arg;
            if (ref.indexOf('/db/') !== 0) {
                ref = "/db/".concat(arg);
            }
        }
        else {
            ref = binding_1.Enhancer.getIdentifier(arg);
        }
        return ref;
    };
    /**
     * Return the metamodel entity type representing the entity.
     *
     * @param typeConstructor - the type of the represented entity
     * @return the metamodel entity type or null if the class is not a managed entity
     */
    Metamodel.prototype.entity = function (typeConstructor) {
        var ref = this.getRef(typeConstructor);
        return ref ? this.entities[ref] : null;
    };
    /**
     * Return the metamodel basic type representing the native class.
     * @param typeConstructor - the type of the represented native class
     * @return the metamodel basic type
     */
    Metamodel.prototype.baseType = function (typeConstructor) {
        var ref = null;
        if (typeof typeConstructor === 'string') {
            ref = this.getRef(typeConstructor);
        }
        else {
            var baseTypesNames = Object.keys(this.baseTypes);
            for (var i = 0, len = baseTypesNames.length; i < len; i += 1) {
                var name_1 = baseTypesNames[i];
                var type = this.baseTypes[name_1];
                if (!type.noResolving && type.typeConstructor === typeConstructor) {
                    ref = name_1;
                    break;
                }
            }
        }
        return ref ? this.baseTypes[ref] : null;
    };
    /**
     * Return the metamodel embeddable type representing the embeddable class.
     * @param typeConstructor - the type of the represented embeddable class
     * @return the metamodel embeddable type or null if the class is not a managed embeddable
     */
    Metamodel.prototype.embeddable = function (typeConstructor) {
        var ref = this.getRef(typeConstructor);
        return ref ? this.embeddables[ref] : null;
    };
    /**
     * Return the metamodel managed type representing the entity, mapped superclass, or embeddable class.
     *
     * @param typeConstructor - the type of the represented managed class
     * @return the metamodel managed type
     */
    Metamodel.prototype.managedType = function (typeConstructor) {
        return this.entity(typeConstructor) || this.embeddable(typeConstructor);
    };
    /**
     * @param type
     * @return the added type
     */
    Metamodel.prototype.addType = function (type) {
        var types = {};
        if (type.isBasic) {
            types = this.baseTypes;
        }
        else if (type.isEmbeddable) {
            type.init(this.enhancer);
            types = this.embeddables;
        }
        else if (type.isEntity) {
            var entityType = type;
            entityType.init(this.enhancer);
            types = this.entities;
            if (entityType.superType === null && entityType.ref !== EntityType_1.EntityType.Object.ref) {
                entityType.superType = this.entity(EntityType_1.EntityType.Object.ref);
            }
        }
        if (types[type.ref]) {
            throw new Error("The type ".concat(type.ref, " is already declared."));
        }
        types[type.ref] = type;
        return type;
    };
    /**
     * Load all schema data from the server
     * @return
     */
    Metamodel.prototype.load = function () {
        var _this = this;
        if (!this.isInitialized) {
            return this.withLock(function () {
                var msg = new message.GetAllSchemas();
                return _this.entityManagerFactory.send(msg).then(function (response) {
                    _this.init(response.entity);
                    return _this;
                });
            });
        }
        throw new Error('Metamodel is already initialized.');
    };
    /**
     * Store all local schema data on the server, or the provided one
     *
     * Note: The schema must be initialized, by init or load
     *
     * @param managedType The specific type to persist, if omitted the complete schema
     * will be updated
     * @return
     */
    Metamodel.prototype.save = function (managedType) {
        var _this = this;
        return this.sendUpdate(managedType || this.toJSON()).then(function () { return _this; });
    };
    /**
     * Update the metamodel with the schema
     *
     * The provided data object will be forwarded to the UpdateAllSchemas resource.
     * The underlying schema of this Metamodel object will be replaced by the result.
     *
     * @param data The JSON which will be send to the UpdateAllSchemas resource.
     * @return
     */
    Metamodel.prototype.update = function (data) {
        var _this = this;
        return this.sendUpdate(data).then(function (response) {
            _this.fromJSON(response.entity);
            return _this;
        });
    };
    Metamodel.prototype.sendUpdate = function (data) {
        var _this = this;
        return this.withLock(function () {
            var msg;
            if (data instanceof ManagedType_1.ManagedType) {
                msg = new message.UpdateSchema(data.name, data.toJSON());
            }
            else {
                msg = new message.UpdateAllSchemas(data);
            }
            return _this.entityManagerFactory.send(msg);
        });
    };
    /**
     * Get the current schema types as json
     * @return the json data
     */
    Metamodel.prototype.toJSON = function () {
        var _this = this;
        if (!this.isInitialized) {
            throw new Error('Metamodel is not initialized.');
        }
        return [].concat(Object.keys(this.entities).map(function (ref) { return _this.entities[ref].toJSON(); }), Object.keys(this.embeddables).map(function (ref) { return _this.embeddables[ref].toJSON(); }));
    };
    /**
     * Replace the current schema by the provided one in json
     * @param json The json schema data
     * @return
     */
    Metamodel.prototype.fromJSON = function (json) {
        var _this = this;
        var builder = new ModelBuilder_1.ModelBuilder();
        var models = builder.buildModels(json);
        this.baseTypes = {};
        this.embeddables = {};
        this.entities = {};
        Object.keys(models).forEach(function (ref) { return _this.addType(models[ref]); });
    };
    /**
     * Creates an index
     *
     * @param bucket Name of the Bucket
     * @param index Will be applied for the given bucket
     * @return
     */
    Metamodel.prototype.createIndex = function (bucket, index) {
        var msg = new message.CreateDropIndex(bucket, tslib_1.__assign(tslib_1.__assign({}, index.toJSON()), { drop: false }));
        return this.entityManagerFactory.send(msg);
    };
    /**
     * Drops an index
     *
     * @param bucket Name of the Bucket
     * @param index Will be dropped for the given bucket
     * @return
     */
    Metamodel.prototype.dropIndex = function (bucket, index) {
        var msg = new message.CreateDropIndex(bucket, tslib_1.__assign(tslib_1.__assign({}, index.toJSON()), { drop: true }));
        return this.entityManagerFactory.send(msg);
    };
    /**
     * Drops all indexes
     *
     * @param bucket Indexes will be dropped for the given bucket
     * @return
     */
    Metamodel.prototype.dropAllIndexes = function (bucket) {
        var msg = new message.DropAllIndexes(bucket);
        return this.entityManagerFactory.send(msg);
    };
    /**
     * Loads all indexes for the given bucket
     *
     * @param bucket Current indexes will be loaded for the given bucket
     * @return
     */
    Metamodel.prototype.getIndexes = function (bucket) {
        var msg = new message.ListIndexes(bucket);
        return this.entityManagerFactory.send(msg)
            .then(function (response) { return response.entity.map(function (el) { return new DbIndex_1.DbIndex(el.keys, el.unique); }); })
            .catch(function (e) {
            if (e.status === connector_1.StatusCode.BUCKET_NOT_FOUND || e.status === connector_1.StatusCode.OBJECT_NOT_FOUND) {
                return null;
            }
            throw e;
        });
    };
    return Metamodel;
}(util_1.Lockable));
exports.Metamodel = Metamodel;
//# sourceMappingURL=data:application/json;base64,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